import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { apiUrl, ensureApiConfig } from '../lib/apiBase';

const inquiryOptions = [
  '— Select —',
  'Color Formulation',
  'White Masterbatch',
  'UV / Additive MB',
  'NIR Absorber',
  'Technical Consultation',
  'Partnership',
  'Trend Intelligence Report',
];

const polymerOptions = [
  '— Select —',
  'PET',
  'rPET',
  'PP',
  'BOPP',
  'PE',
  'HDPE',
  'LDPE',
  'LLDPE',
  'ABS',
  'PS',
  'HIPS',
  'PC',
  'PA',
  'PMMA',
  'POM',
  'PBT',
  'PLA',
  'PBAT',
  'PVC',
  'Not Sure',
];

const countryCodes = [
  { code: '+91', iso: 'in', label: 'India (+91)' },
  { code: '+971', iso: 'ae', label: 'UAE (+971)' },
  { code: '+65', iso: 'sg', label: 'Singapore (+65)' },
  { code: '+1', iso: 'us', label: 'USA / Canada (+1)' },
  { code: '+44', iso: 'gb', label: 'United Kingdom (+44)' },
  { code: '+49', iso: 'de', label: 'Germany (+49)' },
  { code: '+33', iso: 'fr', label: 'France (+33)' },
  { code: '+39', iso: 'it', label: 'Italy (+39)' },
  { code: '+34', iso: 'es', label: 'Spain (+34)' },
  { code: '+31', iso: 'nl', label: 'Netherlands (+31)' },
  { code: '+32', iso: 'be', label: 'Belgium (+32)' },
  { code: '+41', iso: 'ch', label: 'Switzerland (+41)' },
  { code: '+46', iso: 'se', label: 'Sweden (+46)' },
  { code: '+47', iso: 'no', label: 'Norway (+47)' },
  { code: '+45', iso: 'dk', label: 'Denmark (+45)' },
  { code: '+48', iso: 'pl', label: 'Poland (+48)' },
  { code: '+90', iso: 'tr', label: 'Turkey (+90)' },
  { code: '+7', iso: 'ru', label: 'Russia (+7)' },
  { code: '+86', iso: 'cn', label: 'China (+86)' },
  { code: '+81', iso: 'jp', label: 'Japan (+81)' },
  { code: '+82', iso: 'kr', label: 'South Korea (+82)' },
  { code: '+66', iso: 'th', label: 'Thailand (+66)' },
  { code: '+84', iso: 'vn', label: 'Vietnam (+84)' },
  { code: '+62', iso: 'id', label: 'Indonesia (+62)' },
  { code: '+60', iso: 'my', label: 'Malaysia (+60)' },
  { code: '+63', iso: 'ph', label: 'Philippines (+63)' },
  { code: '+880', iso: 'bd', label: 'Bangladesh (+880)' },
  { code: '+94', iso: 'lk', label: 'Sri Lanka (+94)' },
  { code: '+977', iso: 'np', label: 'Nepal (+977)' },
  { code: '+92', iso: 'pk', label: 'Pakistan (+92)' },
  { code: '+966', iso: 'sa', label: 'Saudi Arabia (+966)' },
  { code: '+974', iso: 'qa', label: 'Qatar (+974)' },
  { code: '+973', iso: 'bh', label: 'Bahrain (+973)' },
  { code: '+968', iso: 'om', label: 'Oman (+968)' },
  { code: '+965', iso: 'kw', label: 'Kuwait (+965)' },
  { code: '+20', iso: 'eg', label: 'Egypt (+20)' },
  { code: '+27', iso: 'za', label: 'South Africa (+27)' },
  { code: '+234', iso: 'ng', label: 'Nigeria (+234)' },
  { code: '+254', iso: 'ke', label: 'Kenya (+254)' },
  { code: '+61', iso: 'au', label: 'Australia (+61)' },
  { code: '+64', iso: 'nz', label: 'New Zealand (+64)' },
  { code: '+55', iso: 'br', label: 'Brazil (+55)' },
  { code: '+52', iso: 'mx', label: 'Mexico (+52)' },
];

function flagSrc(iso) {
  return `https://flagcdn.com/${iso}.svg`;
}

function CountryFlag({ iso }) {
  return (
    <img
      className="contact-country__flag"
      src={flagSrc(iso)}
      alt=""
      width={22}
      height={16}
      loading="lazy"
      decoding="async"
    />
  );
}

function CountryCodeSelect({ id, countryCode, onChange }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const selected = countryCodes.find((c) => c.code === countryCode) || countryCodes[0];

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="contact-country" ref={wrapRef}>
      <select
        id={id}
        aria-label="Country code"
        className="contact-country__native"
        required
        tabIndex={-1}
        value={countryCode}
        onChange={(event) => onChange(event.target.value)}
      >
        {countryCodes.map((c) => (
          <option key={c.code} value={c.code}>
            {c.label}
          </option>
        ))}
      </select>
      <button
        type="button"
        className="contact-country__btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={selected.label}
        onClick={() => setOpen((value) => !value)}
      >
        <CountryFlag iso={selected.iso} />
        <span className="contact-country__label">{selected.label}</span>
        <span className="contact-country__caret" aria-hidden="true" />
      </button>
      {open ? (
        <ul className="contact-country__list" role="listbox" aria-label="Country codes">
          {countryCodes.map((c) => (
            <li key={c.code} role="none">
              <button
                type="button"
                role="option"
                aria-selected={c.code === countryCode}
                className="contact-country__opt"
                onClick={() => {
                  onChange(c.code);
                  setOpen(false);
                }}
              >
                <CountryFlag iso={c.iso} />
                <span>{c.label}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function digitsOnly(value) {
  return String(value || '').replace(/\D/g, '');
}

function phoneMaxDigits(code) {
  return code === '+91' ? 10 : 15;
}

function isValidPhone(digits, code) {
  if (code === '+91') return /^\d{10}$/.test(digits);
  return /^\d{7,15}$/.test(digits);
}

function phonePlaceholder(code) {
  return code === '+91' ? 'XXXXXXXXXX' : 'XXXXXXXX';
}

export default function ContactForm({ modern = false }) {
  const [searchParams] = useSearchParams();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [phone, setPhone] = useState('');
  const [inquiryType, setInquiryType] = useState(inquiryOptions[0]);
  const [polymerType, setPolymerType] = useState(polymerOptions[0]);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const market = searchParams.get('market');
    const polymer = searchParams.get('polymer');
    const process = searchParams.get('process');
    const need = searchParams.get('need');
    const inquiry = searchParams.get('inquiry');
    if (polymer) {
      const needle = polymer.trim();
      const exact = polymerOptions.find((o) => o.toLowerCase() === needle.toLowerCase());
      if (exact) {
        setPolymerType(exact);
      } else {
        const tokens = needle.split(/[\s/,()]+/).filter(Boolean);
        const match = polymerOptions.find((o) => o !== polymerOptions[0] && tokens.includes(o));
        if (match) setPolymerType(match);
      }
    }
    if (inquiry === 'intelligence-report') setInquiryType('Trend Intelligence Report');
    else if (need) setInquiryType('Technical Consultation');
    if (market || polymer || process || need) {
      const prefill = [
        market && `Market: ${market}`,
        polymer && `Polymer: ${polymer}`,
        process && `Converting Process: ${process}`,
        need && `Need: ${need}`,
      ]
        .filter(Boolean)
        .join('\n');
      setMessage((prev) => (prev ? prev : prefill));
    }
  }, [searchParams]);

  function handleCountryChange(next) {
    setCountryCode(next);
    setPhone((prev) => digitsOnly(prev).slice(0, phoneMaxDigits(next)));
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    const phoneDigits = digitsOnly(phone);
    if (!countryCode || !isValidPhone(phoneDigits, countryCode)) {
      toast.error(
        countryCode === '+91'
          ? 'Enter a valid Indian contact number (10 digits).'
          : 'Enter a valid contact number (7–15 digits) with country code.'
      );
      return;
    }
    setSubmitting(true);
    await ensureApiConfig();
    const endpoint = apiUrl('/api/contact');
    try {
      const payload = {
        firstName,
        lastName,
        company,
        email,
        countryCode,
        phone: phoneDigits,
        inquiryType: inquiryType === inquiryOptions[0] ? '' : inquiryType,
        polymerType: polymerType === polymerOptions[0] ? '' : polymerType,
        message,
      };
      const { data } = await axios.post(endpoint, payload, {
        timeout: 30000,
        headers: { 'Content-Type': 'application/json' },
      });
      if (data?.ok) {
        const dbHint =
          ' Set MYSQL_HOST, MYSQL_USER, MYSQL_PASSWORD, and MYSQL_DATABASE in server/.env, then restart the API (cd server → npm start).';
        if (data.stored) {
          if (data.emailSent) {
            toast.success(
              'Inquiry saved. A confirmation email is on its way to your inbox.'
            );
          } else {
            toast.success(
              'Inquiry saved. We will respond soon. (Add SMTP in server/.env to email a confirmation.)'
            );
          }
        } else if (data.emailSent) {
          toast.warn(
            `Confirmation email was sent, but the inquiry was not saved — MySQL is not active on the server.${dbHint}`
          );
        } else {
          toast.warn(
            `${data.note || 'Inquiry was not saved to the database.'}${dbHint}`
          );
        }
      } else {
        toast.error('Could not send inquiry. Please try again.');
      }
    } catch (err) {
      let msg = err?.response?.data?.error || err.message || 'Request failed';
      if (err.response?.status === 404) {
        msg =
          `API not found (${endpoint}). Production: run the Node server with client/build, or set REACT_APP_API_URL at build time, or add config.json with "apiUrl" (see client/public/config.json.example).`;
      } else if (!err.response) {
        const code = err.code || '';
        const isNetwork =
          code === 'ERR_NETWORK' ||
          code === 'ECONNABORTED' ||
          (typeof err.message === 'string' && err.message.toLowerCase().includes('network'));
        if (isNetwork) {
          msg = `Cannot reach the API (${endpoint}). Start the backend: cd server → npm start (port 5000), keep this site on npm start in client. Or set REACT_APP_API_URL to your API base.`;
        }
      }
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className={modern ? 'contact-form-modern' : 'rr'} onSubmit={onSubmit}>
      {modern ? (
        <>
          <div className="contact-fld-card" data-accent="gold">
            <label htmlFor="fn">First Name</label>
            <input
              id="fn"
              type="text"
              placeholder="John"
              required
              value={firstName}
              onChange={(ev) => setFirstName(ev.target.value)}
            />
          </div>
          <div className="contact-fld-card" data-accent="blue">
            <label htmlFor="ln">Last Name</label>
            <input
              id="ln"
              type="text"
              placeholder="Doe"
              required
              value={lastName}
              onChange={(ev) => setLastName(ev.target.value)}
            />
          </div>
          <div className="contact-fld-card" data-accent="green">
            <label htmlFor="co">Company</label>
            <input
              id="co"
              type="text"
              placeholder="Your organization"
              required
              value={company}
              onChange={(ev) => setCompany(ev.target.value)}
            />
          </div>
          <div className="contact-fld-card" data-accent="navy">
            <label htmlFor="em">Email</label>
            <input
              id="em"
              type="email"
              placeholder="john@company.com"
              required
              value={email}
              onChange={(ev) => setEmail(ev.target.value)}
            />
          </div>
          <div className="contact-fld-card contact-fld-card--phone" data-accent="gold">
            <label htmlFor="ph">Contact Number</label>
            <div className="contact-phone">
              <CountryCodeSelect id="cc" countryCode={countryCode} onChange={handleCountryChange} />
              <input
                id="ph"
                type="tel"
                inputMode="numeric"
                autoComplete="tel-national"
                placeholder={phonePlaceholder(countryCode)}
                required
                minLength={countryCode === '+91' ? 10 : 7}
                maxLength={phoneMaxDigits(countryCode)}
                pattern={countryCode === '+91' ? '[0-9]{10}' : '[0-9]{7,15}'}
                title={
                  countryCode === '+91'
                    ? 'Enter 10 digits only'
                    : 'Enter 7 to 15 digits only'
                }
                value={phone}
                onChange={(ev) =>
                  setPhone(digitsOnly(ev.target.value).slice(0, phoneMaxDigits(countryCode)))
                }
              />
            </div>
          </div>
          <div className="contact-fld-card" data-accent="cloud">
            <label htmlFor="inq">Inquiry Type</label>
            <select id="inq" value={inquiryType} onChange={(ev) => setInquiryType(ev.target.value)}>
              {inquiryOptions.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>
          <div className="contact-fld-card" data-accent="white">
            <label htmlFor="pol">Polymer Type</label>
            <select id="pol" value={polymerType} onChange={(ev) => setPolymerType(ev.target.value)}>
              {polymerOptions.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>
          <div className="contact-fld-card contact-fld-card--wide">
            <label htmlFor="msg">Your Message</label>
            <textarea
              id="msg"
              rows={5}
              placeholder="Describe your application, processing conditions, volume requirements, and any regulatory constraints..."
              value={message}
              onChange={(ev) => setMessage(ev.target.value)}
            />
          </div>
          <div className="contact-form-modern__foot">
            <span className="contact-form-modern__note">We respond within 24 business hours.</span>
            <button type="submit" className="contact-form-modern__submit" disabled={submitting}>
              {submitting ? 'Sending…' : 'Send Inquiry →'}
            </button>
          </div>
        </>
      ) : (
        <>
      <div className="fld">
        <label htmlFor="fn">First Name</label>
        <input
          id="fn"
          type="text"
          placeholder="John"
          required
          value={firstName}
          onChange={(ev) => setFirstName(ev.target.value)}
        />
      </div>
      <div className="fld">
        <label htmlFor="ln">Last Name</label>
        <input
          id="ln"
          type="text"
          placeholder="Doe"
          required
          value={lastName}
          onChange={(ev) => setLastName(ev.target.value)}
        />
      </div>
      <div className="fld">
        <label htmlFor="co">Company</label>
        <input
          id="co"
          type="text"
          placeholder="Your organization"
          required
          value={company}
          onChange={(ev) => setCompany(ev.target.value)}
        />
      </div>
      <div className="fld">
        <label htmlFor="em">Email</label>
        <input
          id="em"
          type="email"
          placeholder="john@company.com"
          required
          value={email}
          onChange={(ev) => setEmail(ev.target.value)}
        />
      </div>
      <div className="fld fld--phone">
        <label htmlFor="ph">Contact Number</label>
        <div className="contact-phone">
          <CountryCodeSelect id="cc" countryCode={countryCode} onChange={handleCountryChange} />
          <input
            id="ph"
            type="tel"
            inputMode="numeric"
            autoComplete="tel-national"
            placeholder={phonePlaceholder(countryCode)}
            required
            minLength={countryCode === '+91' ? 10 : 7}
            maxLength={phoneMaxDigits(countryCode)}
            pattern={countryCode === '+91' ? '[0-9]{10}' : '[0-9]{7,15}'}
            title={
              countryCode === '+91'
                ? 'Enter 10 digits only'
                : 'Enter 7 to 15 digits only'
            }
            value={phone}
            onChange={(ev) =>
              setPhone(digitsOnly(ev.target.value).slice(0, phoneMaxDigits(countryCode)))
            }
          />
        </div>
      </div>
      <div className="fld">
        <label htmlFor="inq">Inquiry Type</label>
        <select id="inq" value={inquiryType} onChange={(ev) => setInquiryType(ev.target.value)}>
          {inquiryOptions.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      </div>
      <div className="fld">
        <label htmlFor="pol">Polymer Type</label>
        <select id="pol" value={polymerType} onChange={(ev) => setPolymerType(ev.target.value)}>
          {polymerOptions.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      </div>
      <div className="fld">
        <label htmlFor="msg">Your Message</label>
        <textarea
          id="msg"
          rows={5}
          placeholder="Describe your application, processing conditions, volume requirements, and any regulatory constraints..."
          value={message}
          onChange={(ev) => setMessage(ev.target.value)}
        />
      </div>
      <div className="f-foot">
        <span className="f-note">We respond within 24 business hours.</span>
        <button type="submit" className="btn-p" disabled={submitting}>
          {submitting ? 'Sending…' : 'Send Inquiry →'}
        </button>
      </div>
        </>
      )}
    </form>
  );
}
